import React, { useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import ProductosTable from '../components/cantina/productos/ProductosTable';
import ProductoFormModal from '../components/cantina/productos/ProductoFormModal';
import StockModal from '../components/cantina/productos/StockModal';
import InventarioPinModal from '../components/cantina/productos/InventarioPinModal';
import MovimientosStockTable from '../components/cantina/productos/MovimientosStockTable';
import CategoriasModal from '../components/cantina/productos/CategoriasModal';
import useCantinaInventario from '../hooks/cantina/useCantinaInventario';
import useCantinaMovimientos from '../hooks/cantina/useCantinaMovimientos';
import useCantinaCategorias from '../hooks/cantina/useCantinaCategorias';

export default function CantinaProductos() {
  const inventario = useCantinaInventario();
  const movimientos = useCantinaMovimientos();
  const categorias = useCantinaCategorias();

  const [vista, setVista] = useState('productos');
  const [productoEditar, setProductoEditar] = useState(null);
  const [productoStock, setProductoStock] = useState(null);
  const [nuevoProducto, setNuevoProducto] = useState(false);
  const [categoriasAbiertas, setCategoriasAbiertas] =
    useState(false);

  const [operacionPendiente, setOperacionPendiente] =
    useState(null);

  const resumen = useMemo(() => {
    const total = inventario.productos.length;

    const bajo = inventario.productos.filter((producto) => {
      const stock = Number(producto.stock_actual || 0);
      const minimo = Number(producto.stock_minimo || 0);
      return stock <= minimo;
    }).length;

    const sinStock = inventario.productos.filter(
      (producto) => Number(producto.stock_actual || 0) <= 0
    ).length;

    return { total, bajo, sinStock };
  }, [inventario.productos]);

  const cerrarProducto = () => {
    setNuevoProducto(false);
    setProductoEditar(null);
    inventario.setError('');
  };

  const cerrarStock = () => {
    setProductoStock(null);
    inventario.setError('');
  };

  const guardarProducto = async (data) => {
    if (productoEditar?.id) {
      const resultado = await inventario.editarProducto(
        productoEditar.id,
        data
      );

      if (resultado.ok) {
        cerrarProducto();
      }

      return;
    }

    setOperacionPendiente({
      tipo: 'crear',
      data,
    });
  };

  const prepararReposicion = (data) => {
    setOperacionPendiente({
      tipo: 'reposicion',
      productoId: productoStock.id,
      data,
    });
  };

  const prepararAjuste = (data) => {
    setOperacionPendiente({
      tipo: 'ajuste',
      productoId: productoStock.id,
      data,
    });
  };

  const confirmarConUsuario = async (usuario, pin) => {
    if (!operacionPendiente) return;

    let resultado;

    if (operacionPendiente.tipo === 'crear') {
      resultado = await inventario.crearProducto({
        ...operacionPendiente.data,
        usuario_id: usuario.id,
      });
    }

    if (operacionPendiente.tipo === 'reposicion') {
      resultado = await inventario.reponerStock(
        operacionPendiente.productoId,
        {
          ...operacionPendiente.data,
          usuario_id: usuario.id,
        }
      );
    }

    if (operacionPendiente.tipo === 'ajuste') {
      // El backend valida el PIN y exige rol administrador.
      resultado = await inventario.ajustarStock(
        operacionPendiente.productoId,
        {
          ...operacionPendiente.data,
          autorizacionPin: pin,
        }
      );
    }

    if (!resultado?.ok) {
      throw new Error(
        resultado?.error || 'No se pudo completar la operación.'
      );
    }

    setOperacionPendiente(null);
    setNuevoProducto(false);
    setProductoEditar(null);
    setProductoStock(null);
  };

  const tituloPin =
    operacionPendiente?.tipo === 'crear'
      ? 'Confirmar nuevo producto'
      : operacionPendiente?.tipo === 'reposicion'
        ? 'Confirmar reposición'
        : 'Confirmar ajuste de stock';

  const esAjuste = operacionPendiente?.tipo === 'ajuste';

  return (
    <>
      <Navbar />

      <main className="min-h-screen bg-gradient-to-br from-azul-oscuro to-azul-lago p-4 md:p-8">
        <div className="mx-auto max-w-[1500px]">
          <header className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amarillo-dorado">
                Driza Camp · Cantina
              </p>

              <h1 className="mt-1 text-3xl font-bold text-white">
                Productos e inventario
              </h1>

              <p className="mt-1 text-sm text-white/75">
                Precios, stock y reposiciones de Cantina.
              </p>
            </div>

            <div className="flex gap-2">
              <a
                href="/ventas"
                className="rounded-xl border border-white/30 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/20"
              >
                ← Volver a venta
              </a>

              <button
                type="button"
                onClick={() => setCategoriasAbiertas(true)}
                className="rounded-xl border border-amarillo-dorado/60 bg-white/10 px-4 py-2.5 text-sm font-semibold text-amarillo-dorado transition hover:bg-white/20"
              >
                Categorías
              </button>

              <button
                type="button"
                onClick={() => {
                  inventario.setError('');
                  setNuevoProducto(true);
                }}
                className="rounded-xl bg-amarillo-dorado px-5 py-2.5 text-sm font-bold text-azul-oscuro shadow-lg transition hover:bg-amber-400"
              >
                + Nuevo producto
              </button>
            </div>
          </header>

          <section className="mb-5 grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-white/10 px-4 py-3 text-white">
              <div className="text-[11px] uppercase text-white/60">
                Productos
              </div>
              <strong className="text-2xl">
                {resumen.total}
              </strong>
            </div>

            <div className="rounded-xl bg-white/10 px-4 py-3 text-white">
              <div className="text-[11px] uppercase text-white/60">
                Stock bajo
              </div>
              <strong className="text-2xl text-amarillo-dorado">
                {resumen.bajo}
              </strong>
            </div>

            <div className="rounded-xl bg-white/10 px-4 py-3 text-white">
              <div className="text-[11px] uppercase text-white/60">
                Sin stock
              </div>
              <strong className="text-2xl text-red-200">
                {resumen.sinStock}
              </strong>
            </div>
          </section>

          <div className="mb-4 flex gap-2">
            <button
              type="button"
              onClick={() => setVista('productos')}
              className={`rounded-xl px-4 py-2.5 text-sm font-bold transition ${
                vista === 'productos'
                  ? 'bg-amarillo-dorado text-azul-oscuro shadow-lg'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              Productos
            </button>

            <button
              type="button"
              onClick={() => {
                setVista('movimientos');

                if (!movimientos.cargado) {
                  movimientos.cargarMovimientos();
                }
              }}
              className={`rounded-xl px-4 py-2.5 text-sm font-bold transition ${
                vista === 'movimientos'
                  ? 'bg-amarillo-dorado text-azul-oscuro shadow-lg'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              Movimientos
            </button>
          </div>

          {vista === 'productos' ? (
            <>
              <section className="mb-4 rounded-2xl border border-blanco-puro/20 bg-azul-oscuro/60 p-4 shadow-xl backdrop-blur">
                <input
                  value={inventario.busqueda}
                  onChange={(event) =>
                    inventario.setBusqueda(event.target.value)
                  }
                  placeholder="Buscar por nombre, código o código de barras..."
                  className="h-11 w-full rounded-xl border border-blanco-puro/20 bg-azul-oscuro/70 px-4 text-sm text-blanco-puro placeholder:text-blanco-puro/50 outline-none transition focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/40"
                />
              </section>

              <ProductosTable
                productos={inventario.productos}
                cargando={inventario.cargando}
                onEditar={(producto) => {
                  inventario.setError('');
                  setProductoEditar(producto);
                }}
                onStock={(producto) => {
                  inventario.setError('');
                  setProductoStock(producto);
                }}
              />
            </>
          ) : (
            <MovimientosStockTable
              movimientos={movimientos.movimientos}
              cargando={movimientos.cargando}
              error={movimientos.error}
            />
          )}
        </div>
      </main>

      <ProductoFormModal
        abierto={nuevoProducto || Boolean(productoEditar)}
        producto={productoEditar}
        categorias={categorias.categorias}
        guardando={inventario.guardando}
        error={inventario.error}
        onClose={cerrarProducto}
        onSubmit={guardarProducto}
      />

      <CategoriasModal
        abierto={categoriasAbiertas}
        categorias={categorias.categorias}
        cargando={categorias.cargando}
        guardando={categorias.guardando}
        error={categorias.error}
        onClose={() => setCategoriasAbiertas(false)}
        onCrear={categorias.crear}
        onEditar={categorias.editar}
        onEliminar={async (id) => {
          const resultado = await categorias.eliminar(id);

          if (resultado?.ok) {
            await inventario.recargar();
          }

          return resultado;
        }}
      />

      <StockModal
        abierto={Boolean(productoStock)}
        producto={productoStock}
        guardando={inventario.guardando}
        error={inventario.error}
        onClose={cerrarStock}
        onReposicion={prepararReposicion}
        onAjuste={prepararAjuste}
      />

      <InventarioPinModal
        abierto={Boolean(operacionPendiente)}
        titulo={tituloPin}
        descripcion={
          esAjuste
            ? 'Solo un administrador puede ajustar el stock'
            : undefined
        }
        etiquetaPin={esAjuste ? 'PIN de administrador' : undefined}
        procesando={inventario.guardando}
        onClose={() => setOperacionPendiente(null)}
        onConfirm={confirmarConUsuario}
      />
    </>
  );
}
